import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useArchivePatientFamilyHistory } from "../../../../../../../queries/history/past-family/use-patient-family-history-archive";
import { usePastFamilyHistoryList } from "../../../../../../../queries/history/past-family/use-patient-family-history-list";
import { FamilyHistory } from "../../../../../../../services";
import { formatDate } from "../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  roleFilter,
} from "../../../../../../../utils/utils";
import {
  convertFamilyHistoryPrefillPayload,
  RELATIVE_LABEL_MAPPER,
} from "../clinical-details/components/ClinicalLayout-constant";
import UpsertFamilyHistory from "./UpsertFamilyHistory";
import { getStoredRealm } from "@/utils/auth-storage";

const PatientFamilyHistoryNew = () => {
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const { patientId } = useParams() as { patientId: any };
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const {
    data: FamilyHistories,
    refetch,
    isLoading,
    isRefetching,
  } = usePastFamilyHistoryList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    xTENANTID: getStoredRealm() ?? undefined,
  });

  const { mutateAsync: archiveFamilyHistory } =
    useArchivePatientFamilyHistory();
  const deletePastSurgicalDisclosure = useDeleteDisClosure();
  const familyDisclosure = useDisclosure();
  const { hasView, hasUpdate, hasDelete } = useRoleController("Family History");

  const COLUMNS = applyDefaultColumnSettings<FamilyHistory>(
    [
      {
        field: "conditionName",
        headerName: "Condition",
        flex: 0.6,
        minWidth: 180,
        renderCell: ({ value }) =>
          value ? <RenderWithTooltip value={value} /> : "-",
      },
      {
        field: "relative",
        headerName: "Relationship to Patient",
        flex: 0.3,
        minWidth: 110,
        renderCell: ({ value }) =>
          value ? (RELATIVE_LABEL_MAPPER[value] ?? value) : "-",
      },
      {
        field: "onSetAge",
        headerName: "Onset Age",
        flex: 0.2,
        minWidth: 70,
      },
      {
        field: "recordedDate",
        headerName: "Recorded Date",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) => (value ? formatDate(value) : "-"),
      },
      {
        field: "died",
        headerName: "Life Status",
        flex: 0.25,
        minWidth: 90,
        renderCell: ({ value }) =>
          typeof value === "boolean" ? (value ? "Deceased" : "Alive") : "-",
      },
      {
        field: "note",
        headerName: "Note",
        flex: 0.5,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        hideSortIcons: true,
        headerAlign: "center",
        align: "center",
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "edit", label: "Edit" },
                  { value: "delete", label: "Delete" },
                ],
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  familyDisclosure.open(
                    convertFamilyHistoryPrefillPayload(row)
                  );
                } else deletePastSurgicalDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["recordedDate"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveFamilyHistory({
      patientFamilyHistoryId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertFamilyHistory disclosureHost={familyDisclosure} />

      <DeleteModal
        w="380px"
        open={deletePastSurgicalDisclosure.opened}
        onClose={deletePastSurgicalDisclosure.close}
        id={deletePastSurgicalDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Family History"}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        loading={isLoading || isRefetching}
        getRowId={(row) => row?.uuid}
        rows={FamilyHistories?.data?.content ?? []}
        rowCount={FamilyHistories?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, !hasView || isVisitNotePage)}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        sortModel={gridSorting}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default PatientFamilyHistoryNew;
